import { useState, useEffect } from 'react';
import { Map, AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps';
import { Calendar, MapPin, Users, Plus, Search, X, Clock, User, Check, Filter, Lock, Globe, UserPlus, Pencil, History, Building2 } from 'lucide-react';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import CustomDropdown from '../ui/CustomDropdown';
import PageHeader from '../ui/PageHeader';
import DateTimePicker from '../ui/DateTimePicker';
import { useTranslation, useLanguage } from '../../context/LanguageContext';
import { AddressAutocomplete } from '../ui';
import {
    getEvents,
    createEvent,
    updateEvent,
    joinEvent,
    leaveEvent,
    getEventParticipants,
    addEventParticipant,
    addEventGroup,
    EUROPEAN_COUNTRIES
} from '../../services/eventsService';
// import { getVisibleUserContacts } from '../../services/userContactsService';
import { searchPlaces } from '../../services/placesService';
import { searchParticipantsAndGroups } from '../../services/messagesService';
import { getEventTypes, getCategoryTranslation, CATEGORY_DATA } from '../../services/eventsTypesService';


/**
 * Events Module
 * List and map of public events from all members
 */
const Events = () => {
    const { t } = useTranslation();
    const { language } = useLanguage();

    // Current user
    const currentUserId = parseInt(localStorage.getItem('userId') || '0', 10);

    // State
    const [publicEvents, setPublicEvents] = useState([]);
    const [pastEvents, setPastEvents] = useState([]);
    const [invitedEvents, setInvitedEvents] = useState([]);
    const [myEvents, setMyEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [searchKeyword, setSearchKeyword] = useState('');
    const [selectedFilterCategory, setSelectedFilterCategory] = useState(null); // Category filter (like Endroits)
    const [selectedEventTypes, setSelectedEventTypes] = useState([]); // Types within category (excluded ones)
    const [eventTypes, setEventTypes] = useState([]); // All event types flat list
    const [categories, setCategories] = useState([]); // List of categories
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showParticipants, setShowParticipants] = useState(false);
    const [participants, setParticipants] = useState([]);
    const [editingEvent, setEditingEvent] = useState(null); // Event being edited

    // Map center (Europe) - Google Maps format {lat, lng}
    const mapCenter = { lat: 50.8503, lng: 4.3517 }; // Brussels as default center

    // Selected event for InfoWindow
    const [selectedMapEvent, setSelectedMapEvent] = useState(null);

    // DateTimePicker state - shared across modals like in Calendar.jsx
    const [openDatePicker, setOpenDatePicker] = useState(null); // 'start', 'end' or null

    // Load categories and event types on mount
    useEffect(() => {
        loadCategoriesAndTypes();
    }, [language]);

    const loadCategoriesAndTypes = async () => {
        try {
            // Load flat list of event types with all properties
            const typesResponse = await getEventTypes({ lang: language });
            // Handle both array response and {data: [...]} or {DATA: [...]} response (ColdFusion returns uppercase)
            const typesData = Array.isArray(typesResponse) ? typesResponse : (typesResponse?.data || typesResponse?.DATA || []);
            console.log('Loaded event types:', typesData?.length, typesData);
            if (Array.isArray(typesData) && typesData.length > 0) {
                setEventTypes(typesData);
                // Extract unique categories
                const uniqueCategories = [...new Set(typesData.map(t => t.category))].filter(Boolean);
                console.log('Unique categories:', uniqueCategories);
                setCategories(uniqueCategories);
            } else {
                // Fallback to static categories if API returns empty
                console.log('Using fallback categories');
                setCategories(Object.keys(CATEGORY_DATA));
            }
        } catch (error) {
            console.error('Error loading categories:', error);
            // Fallback to static categories on error
            setCategories(Object.keys(CATEGORY_DATA));
        }
    };

    // Get types for a specific category
    const getTypesForCategory = (category) => {
        return eventTypes.filter(t => t.category === category);
    };

    // Select a category filter
    const handleSelectFilterCategory = (category) => {
        setSelectedFilterCategory(category);
        setSelectedEventTypes([]); // Reset type exclusions
    };

    // Toggle type exclusion (deselect/reselect)
    const handleToggleTypeFilter = (type) => {
        const existing = selectedEventTypes.find(t => t.id === type.id);
        if (existing) {
            setSelectedEventTypes(selectedEventTypes.filter(t => t.id !== type.id));
        } else {
            setSelectedEventTypes([...selectedEventTypes, { ...type, excluded: true }]);
        }
    };

    // Exclude all types in category
    const handleExcludeAllTypes = () => {
        const categoryTypes = getTypesForCategory(selectedFilterCategory);
        setSelectedEventTypes(categoryTypes.map(t => ({ ...t, excluded: true })));
    };

    // Include all types in category
    const handleIncludeAllTypes = () => {
        setSelectedEventTypes([]);
    };

    // Load all event categories
    useEffect(() => {
        loadAllEvents();
    }, [selectedCountry, searchKeyword, selectedFilterCategory, selectedEventTypes, currentUserId]);

    const loadAllEvents = async () => {
        setLoading(true);
        const today = new Date().toISOString().split('T')[0];

        // Build common filter options
        const baseFilters = {
            countryCode: selectedCountry,
            keyword: searchKeyword,
            viewerId: currentUserId,
            limit: 100
        };
        // If category selected, filter by active types in that category
        if (selectedFilterCategory) {
            const categoryTypes = getTypesForCategory(selectedFilterCategory);
            const activeTypes = categoryTypes.filter(ct => !selectedEventTypes.find(st => st.id === ct.id && st.excluded));
            // Pass the active type codes to filter
            if (activeTypes.length > 0) {
                baseFilters.typeCodes = activeTypes.map(t => t.code).join(',');
            }
            baseFilters.category = selectedFilterCategory;
        }

        // Load all four categories in parallel
        const [publicResult, pastResult, invitedResult, myResult] = await Promise.all([
            // Public events (upcoming)
            getEvents({
                ...baseFilters,
                startDate: today,
                onlyPublic: true
            }),
            // Past events (all past public events)
            getEvents({
                ...baseFilters,
                endDate: today,
                onlyPublic: true
            }),
            // Invited events (private events where I'm invited but not creator)
            getEvents({
                ...baseFilters,
                startDate: today,
                onlyInvited: true
            }),
            // My events (where I'm creator or participant - past and future)
            getEvents({
                ...baseFilters,
                onlyMyEvents: true
            })
        ]);

        setPublicEvents(publicResult.events);
        setPastEvents(pastResult.events);
        setInvitedEvents(invitedResult.events);
        setMyEvents(myResult.events);
        setLoading(false);
    };

    // Clear all filters (kept for future use)
    const _clearAllFilters = () => {
        setSelectedCountry('');
        setSelectedFilterCategory(null);
        setSelectedEventTypes([]);
        setSearchKeyword('');
    };

    // Clear category filter only
    const handleClearCategoryFilter = () => {
        setSelectedFilterCategory(null);
        setSelectedEventTypes([]);
    };

    // Get all events for map display
    const getAllEventsForMap = () => {
        const allEvents = [...myEvents, ...invitedEvents, ...publicEvents];
        // Remove duplicates by id
        const uniqueEvents = allEvents.filter((event, index, self) =>
            index === self.findIndex(e => e.id === event.id)
        );
        return uniqueEvents;
    };

    const handleJoinEvent = async (event) => {
        const result = await joinEvent(event.id, currentUserId);
        if (result.success) {
            loadAllEvents();
        }
    };

    const handleLeaveEvent = async (event) => {
        const result = await leaveEvent(event.id, currentUserId);
        if (result.success) {
            loadAllEvents();
        }
    };

    const handleShowParticipants = async (event) => {
        setSelectedEvent(event);
        const participantsList = await getEventParticipants(event.id);
        setParticipants(participantsList);
        setShowParticipants(true);
    };

    const isCreator = (event) => event.creatorId === currentUserId;

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
            weekday: 'short',
            day: 'numeric',
            month: 'short'
        });
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Render a single event card (compact version for columns)
    const renderEventCard = (event) => (
        <div
            key={event.id}
            className={`bg-white rounded-lg p-3 shadow-sm border transition-shadow hover:shadow-md cursor-pointer ${
                !event.isPublic ? 'border-orange-200' : 'border-gray-100'
            }`}
            onClick={() => setSelectedEvent(event)}
        >
            <div className="flex justify-between items-start mb-1">
                <h4 className="font-medium text-gray-800 text-sm line-clamp-1">{event.title}</h4>
                {!event.isPublic && <Lock className="w-3 h-3 text-orange-500 shrink-0" />}
            </div>
            <div className="text-xs text-gray-500 space-y-0.5">
                <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-cabaza-purple" />
                    <span>{formatDate(event.startDate)}</span>
                    {event.startDate && <span className="text-gray-400">• {formatTime(event.startDate)}</span>}
                </div>
                <div className="flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-cabaza-purple" />
                    <span className="line-clamp-1">{event.placeName}</span>
                </div>
            </div>
            <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-400">
                    {event.participantCount} <Users className="w-3 h-3 inline" />
                </span>
                {isCreator(event) && (
                    <span className="text-xs bg-cabaza-purple/10 text-cabaza-purple px-1.5 py-0.5 rounded">
                        {language === 'fr' ? 'Org.' : 'Org.'}
                    </span>
                )}
            </div>
        </div>
    );

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <PageHeader
                moduleCode="EVENTS"
                title={t.events?.title || (language === 'fr' ? 'Événements' : 'Events')}
                subtitle={t.events?.subtitle || (language === 'fr' ? 'Découvrez et créez des événements' : 'Discover and create events')}
            >
                <Button onClick={() => setShowCreateModal(true)} icon={Plus}>
                    {language === 'fr' ? 'Créer un événement' : 'Create Event'}
                </Button>
            </PageHeader>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
                {/* Country filter */}
                <div className="relative flex-1">
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10 pointer-events-none" />
                    <CustomDropdown
                        value={selectedCountry}
                        onChange={(val) => setSelectedCountry(val)}
                        options={[
                            { value: '', label: language === 'fr' ? 'Tous les pays' : 'All countries' },
                            ...EUROPEAN_COUNTRIES.map(country => ({
                                value: country.code,
                                label: language === 'fr' ? country.name : country.nameEn
                            }))
                        ]}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg hover:border-cabaza-purple focus:ring-2 focus:ring-cabaza-purple"
                    />
                </div>

                {/* Category filter */}
                <div className="relative flex-1">
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10 pointer-events-none" />
                    <CustomDropdown
                        value={selectedFilterCategory || ''}
                        onChange={(val) => handleSelectFilterCategory(val || null)}
                        options={[
                            { value: '', label: language === 'fr' ? 'Toutes les catégories' : 'All categories' },
                            ...categories.map(category => ({
                                value: category,
                                label: getCategoryTranslation(category, language)
                            }))
                        ]}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg hover:border-cabaza-purple focus:ring-2 focus:ring-cabaza-purple"
                    />
                </div>

                {/* Search */}
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder={language === 'fr' ? 'Rechercher par nom, lieu...' : 'Search by name, place...'}
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                    />
                </div>
            </div>

            {/* Selected category tag + types (like Endroits) */}
            {selectedFilterCategory && (
                <div className="space-y-3 mb-4">
                    {/* Category tag with actions */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <span
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-purple-100 text-purple-700"
                        >
                            {getCategoryTranslation(selectedFilterCategory, language)}
                            <button
                                onClick={handleClearCategoryFilter}
                                className="hover:bg-purple-200 rounded-full p-0.5"
                                title={language === 'fr' ? 'Retirer la catégorie' : 'Remove category'}
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </span>
                        {/* Toggle all types buttons */}
                        <button
                            onClick={handleExcludeAllTypes}
                            className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                            title={language === 'fr' ? 'Tout désélectionner' : 'Deselect all'}
                        >
                            {language === 'fr' ? 'Tout désélectionner' : 'Deselect all'}
                        </button>
                        <button
                            onClick={handleIncludeAllTypes}
                            className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                            title={language === 'fr' ? 'Tout sélectionner' : 'Select all'}
                        >
                            {language === 'fr' ? 'Tout sélectionner' : 'Select all'}
                        </button>
                    </div>

                    {/* Types within category - clickable to exclude/include */}
                    <div className="flex flex-wrap gap-2">
                        {getTypesForCategory(selectedFilterCategory).map(type => {
                            const isExcluded = selectedEventTypes.find(t => t.id === type.id && t.excluded);
                            return (
                                <button
                                    key={type.id}
                                    onClick={() => handleToggleTypeFilter(type)}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                        isExcluded
                                            ? 'bg-gray-100 text-gray-400 line-through'
                                            : 'text-white'
                                    }`}
                                    style={!isExcluded ? { backgroundColor: type.color || '#6366f1' } : {}}
                                >
                                    {type.emoji && <span>{type.emoji}</span>}
                                    {type.name}
                                    {isExcluded && <X className="w-3.5 h-3.5 ml-1" />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Map - Full width with reduced z-index */}
            <div className="h-[250px] rounded-xl overflow-hidden shadow-md mb-4 relative z-0">
                <Map
                    defaultCenter={mapCenter}
                    defaultZoom={5}
                    mapId="events-map"
                    gestureHandling="greedy"
                    disableDefaultUI={false}
                    style={{ width: '100%', height: '100%' }}
                >
                    {getAllEventsForMap().filter(e => e.latitude && e.longitude).map(event => (
                        <AdvancedMarker
                            key={event.id}
                            position={{ lat: event.latitude, lng: event.longitude }}
                            onClick={() => setSelectedMapEvent(event)}
                        >
                            <div className="w-8 h-8 bg-cabaza-purple rounded-full flex items-center justify-center shadow-lg border-2 border-white cursor-pointer hover:scale-110 transition-transform">
                                <Calendar className="w-4 h-4 text-white" />
                            </div>
                        </AdvancedMarker>
                    ))}
                    {selectedMapEvent && (
                        <InfoWindow
                            position={{ lat: selectedMapEvent.latitude, lng: selectedMapEvent.longitude }}
                            onCloseClick={() => setSelectedMapEvent(null)}
                        >
                            <div className="min-w-[200px] p-2">
                                <div className="flex items-center gap-2 mb-1">
                                    <h3 className="font-bold text-gray-800">{selectedMapEvent.title}</h3>
                                    {!selectedMapEvent.isPublic && <Lock className="w-3 h-3 text-orange-500" />}
                                </div>
                                <p className="text-sm text-gray-600">{formatDate(selectedMapEvent.startDate)}</p>
                                {selectedMapEvent.startDate && (
                                    <p className="text-sm text-gray-500">{formatTime(selectedMapEvent.startDate)}</p>
                                )}
                                <p className="text-sm text-cabaza-purple mt-1">{selectedMapEvent.placeName}</p>
                                <p className="text-xs text-gray-500">{selectedMapEvent.placeAddress}</p>
                            </div>
                        </InfoWindow>
                    )}
                </Map>
            </div>

            {/* 4 Columns for event categories */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 min-h-0 overflow-hidden">
                {/* Column 1: Public Events */}
                <div className="flex flex-col min-h-0">
                    <div className="flex items-center gap-2 mb-2 px-1">
                        <Globe className="w-4 h-4 text-green-600" />
                        <h3 className="font-semibold text-gray-700 text-sm">
                            {language === 'fr' ? 'Publics' : 'Public'}
                        </h3>
                        <span className="text-xs bg-green-100 text-green-600 px-1.5 py-0.5 rounded-full">
                            {publicEvents.length}
                        </span>
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                        {loading ? (
                            <div className="flex items-center justify-center h-20">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cabaza-purple"></div>
                            </div>
                        ) : publicEvents.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-4">
                                {language === 'fr' ? 'Aucun événement' : 'No events'}
                            </p>
                        ) : (
                            publicEvents.map(event => renderEventCard(event))
                        )}
                    </div>
                </div>

                {/* Column 2: Past Events */}
                <div className="flex flex-col min-h-0">
                    <div className="flex items-center gap-2 mb-2 px-1">
                        <History className="w-4 h-4 text-gray-500" />
                        <h3 className="font-semibold text-gray-700 text-sm">
                            {language === 'fr' ? 'Passés' : 'Past'}
                        </h3>
                        <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
                            {pastEvents.length}
                        </span>
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                        {loading ? (
                            <div className="flex items-center justify-center h-20">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cabaza-purple"></div>
                            </div>
                        ) : pastEvents.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-4">
                                {language === 'fr' ? 'Aucun événement' : 'No events'}
                            </p>
                        ) : (
                            pastEvents.map(event => renderEventCard(event))
                        )}
                    </div>
                </div>

                {/* Column 3: Invitations */}
                <div className="flex flex-col min-h-0">
                    <div className="flex items-center gap-2 mb-2 px-1">
                        <UserPlus className="w-4 h-4 text-orange-500" />
                        <h3 className="font-semibold text-gray-700 text-sm">
                            {language === 'fr' ? 'Invitations' : 'Invitations'}
                        </h3>
                        <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-full">
                            {invitedEvents.length}
                        </span>
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                        {loading ? (
                            <div className="flex items-center justify-center h-20">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cabaza-purple"></div>
                            </div>
                        ) : invitedEvents.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-4">
                                {language === 'fr' ? 'Aucune invitation' : 'No invitations'}
                            </p>
                        ) : (
                            invitedEvents.map(event => renderEventCard(event))
                        )}
                    </div>
                </div>

                {/* Column 4: My Events */}
                <div className="flex flex-col min-h-0">
                    <div className="flex items-center gap-2 mb-2 px-1">
                        <Calendar className="w-4 h-4 text-cabaza-purple" />
                        <h3 className="font-semibold text-gray-700 text-sm">
                            {language === 'fr' ? 'Mes événements' : 'My Events'}
                        </h3>
                        <span className="text-xs bg-cabaza-purple/20 text-cabaza-purple px-1.5 py-0.5 rounded-full">
                            {myEvents.length}
                        </span>
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                        {loading ? (
                            <div className="flex items-center justify-center h-20">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cabaza-purple"></div>
                            </div>
                        ) : myEvents.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-4">
                                {language === 'fr' ? 'Aucun événement' : 'No events'}
                            </p>
                        ) : (
                            myEvents.map(event => renderEventCard(event))
                        )}
                    </div>
                </div>
            </div>

            {/* Create Event Modal */}
            {showCreateModal && (
                <CreateEventModal
                    onClose={() => {
                        setShowCreateModal(false);
                        setOpenDatePicker(null);
                    }}
                    onEventCreated={() => {
                        setShowCreateModal(false);
                        setOpenDatePicker(null);
                        loadAllEvents();
                    }}
                    currentUserId={currentUserId}
                    language={language}
                    openDatePicker={openDatePicker}
                    setOpenDatePicker={setOpenDatePicker}
                />
            )}

            {/* Edit Event Modal */}
            {editingEvent && (
                <EditEventModal
                    event={editingEvent}
                    onClose={() => {
                        setEditingEvent(null);
                        setOpenDatePicker(null);
                    }}
                    onEventUpdated={() => {
                        setEditingEvent(null);
                        setOpenDatePicker(null);
                        loadAllEvents();
                    }}
                    currentUserId={currentUserId}
                    language={language}
                    openDatePicker={openDatePicker}
                    setOpenDatePicker={setOpenDatePicker}
                />
            )}

            {/* Participants Modal */}
            <Modal
                isOpen={showParticipants && !!selectedEvent}
                onClose={() => setShowParticipants(false)}
                title={language === 'fr' ? 'Participants' : 'Participants'}
                icon={Users}
                size="sm"
                hideFooter
            >
                {selectedEvent && (
                    <>
                        <h3 className="text-lg font-medium text-cabaza-purple mb-4">{selectedEvent.title}</h3>

                        {participants.length === 0 ? (
                            <p className="text-gray-500 text-center py-4">
                                {language === 'fr' ? 'Aucun participant pour le moment' : 'No participants yet'}
                            </p>
                        ) : (
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {participants.map(p => (
                                    <div key={p.id} className="flex items-center justify-between gap-3 p-2 bg-gray-50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-cabaza-purple/20 rounded-full flex items-center justify-center">
                                                <User className="w-4 h-4 text-cabaza-purple" />
                                            </div>
                                            <span className="font-medium text-gray-800">
                                                {p.firstName} {p.lastName}
                                            </span>
                                        </div>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                                            p.status === 'confirmed' ? 'bg-green-100 text-green-600' :
                                            p.status === 'invited' ? 'bg-orange-100 text-orange-600' :
                                            p.status === 'declined' ? 'bg-red-100 text-red-600' :
                                            'bg-gray-100 text-gray-600'
                                        }`}>
                                            {p.status === 'confirmed' && (language === 'fr' ? 'Confirmé' : 'Confirmed')}
                                            {p.status === 'invited' && (language === 'fr' ? 'Invité' : 'Invited')}
                                            {p.status === 'declined' && (language === 'fr' ? 'Décliné' : 'Declined')}
                                            {p.status === 'maybe' && (language === 'fr' ? 'Peut-être' : 'Maybe')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </Modal>

            {/* Event Detail Modal */}
            <Modal
                isOpen={!!selectedEvent && !showParticipants}
                onClose={() => setSelectedEvent(null)}
                title={selectedEvent?.title || ''}
                icon={Calendar}
                size="md"
                hideFooter
            >
                {selectedEvent && (
                    <div className="space-y-4">
                        {/* Event info */}
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 text-gray-600">
                                <Calendar className="w-5 h-5 text-cabaza-purple" />
                                <span>{new Date(selectedEvent.startDate).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                            </div>
                            {selectedEvent.startDate && (
                                <div className="flex items-center gap-2 text-gray-600">
                                    <Clock className="w-5 h-5 text-cabaza-purple" />
                                    <span>{formatTime(selectedEvent.startDate)}{selectedEvent.endDate ? ` - ${formatTime(selectedEvent.endDate)}` : ''}</span>
                                </div>
                            )}
                            <div className="flex items-center gap-2 text-gray-600">
                                <MapPin className="w-5 h-5 text-cabaza-purple" />
                                <div>
                                    <span>{selectedEvent.placeName}</span>
                                    <p className="text-xs text-gray-400">{selectedEvent.placeAddress}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-gray-500">
                                <User className="w-5 h-5 text-gray-400" />
                                <span>{selectedEvent.creatorFirstName} {selectedEvent.creatorLastName}</span>
                            </div>
                        </div>

                        {selectedEvent.description && (
                            <p className="text-gray-600 bg-gray-50 p-3 rounded-lg">{selectedEvent.description}</p>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-4 border-t">
                            <button
                                onClick={() => {
                                    handleShowParticipants(selectedEvent);
                                }}
                                className="flex items-center gap-2 text-gray-600 hover:text-cabaza-purple transition-colors"
                            >
                                <Users className="w-5 h-5" />
                                <span>{selectedEvent.participantCount} {language === 'fr' ? 'participant(s)' : 'participant(s)'}</span>
                            </button>

                            <div className="flex gap-2">
                                {isCreator(selectedEvent) && (
                                    <Button
                                        variant="ghost"
                                        icon={Pencil}
                                        onClick={() => {
                                            setEditingEvent(selectedEvent);
                                            setSelectedEvent(null);
                                        }}
                                    >
                                        {language === 'fr' ? 'Modifier' : 'Edit'}
                                    </Button>
                                )}

                                {!isCreator(selectedEvent) && (
                                    selectedEvent.isParticipating || selectedEvent.myParticipationStatus === 'confirmed' ? (
                                        <Button
                                            variant="ghost"
                                            onClick={() => {
                                                handleLeaveEvent(selectedEvent);
                                                setSelectedEvent(null);
                                            }}
                                        >
                                            <Check className="w-4 h-4 mr-1" />
                                            {language === 'fr' ? 'Inscrit' : 'Joined'}
                                        </Button>
                                    ) : selectedEvent.myParticipationStatus === 'invited' ? (
                                        <Button
                                            onClick={() => {
                                                handleJoinEvent(selectedEvent);
                                                setSelectedEvent(null);
                                            }}
                                        >
                                            <Check className="w-4 h-4 mr-1" />
                                            {language === 'fr' ? 'Accepter' : 'Accept'}
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={() => {
                                                handleJoinEvent(selectedEvent);
                                                setSelectedEvent(null);
                                            }}
                                        >
                                            <Plus className="w-4 h-4 mr-1" />
                                            {language === 'fr' ? 'Participer' : 'Join'}
                                        </Button>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

/**
 * Create Event Modal Component
 */
const CreateEventModal = ({ onClose, onEventCreated, currentUserId, language, openDatePicker, setOpenDatePicker }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        startDate: '',
        endDate: '',
        placeName: '',
        placeAddress: '',
        placeId: null,
        latitude: null,
        longitude: null,
        country: '',
        countryCode: '',
        maxParticipants: 0,
        isPublic: true
    });
    const [saving, setSaving] = useState(false);

    // Place search state
    const [placeSearchQuery, setPlaceSearchQuery] = useState('');
    const [placeSearchResults, setPlaceSearchResults] = useState([]);
    const [searchingPlaces, setSearchingPlaces] = useState(false);
    const [showGoogleFallback, setShowGoogleFallback] = useState(false);

    // Invitation state
    const [inviteSearchQuery, setInviteSearchQuery] = useState('');
    const [inviteSearchResults, setInviteSearchResults] = useState([]);
    const [searchingInvites, setSearchingInvites] = useState(false);
    const [selectedParticipants, setSelectedParticipants] = useState([]); // { id, type: 'contact'|'group', name }
    const [selectedGroups, setSelectedGroups] = useState([]); // { id, name, memberCount }

    // Debounced place search - search public Cabaza places
    useEffect(() => {
        if (placeSearchQuery.length < 2) {
            setPlaceSearchResults([]);
            setShowGoogleFallback(false);
            return;
        }

        const timer = setTimeout(async () => {
            setSearchingPlaces(true);
            try {
                // Search in public Cabaza places
                const results = await searchPlaces(placeSearchQuery, 10);
                setPlaceSearchResults(results);
                setShowGoogleFallback(results.length === 0);
            } catch (error) {
                console.error('Error searching places:', error);
                setPlaceSearchResults([]);
                setShowGoogleFallback(true);
            }
            setSearchingPlaces(false);
        }, 300);

        return () => clearTimeout(timer);
    }, [placeSearchQuery]);

    // Debounced invite search
    useEffect(() => {
        if (inviteSearchQuery.length < 2) {
            setInviteSearchResults([]);
            return;
        }

        const timer = setTimeout(async () => {
            setSearchingInvites(true);
            try {
                const results = await searchParticipantsAndGroups(inviteSearchQuery);
                setInviteSearchResults(results);
            } catch (error) {
                console.error('Error searching participants:', error);
            }
            setSearchingInvites(false);
        }, 300);

        return () => clearTimeout(timer);
    }, [inviteSearchQuery]);

    // Select a public Cabaza place
    const handleSelectPlace = (place) => {
        // Build address from components
        const addressParts = [place.street, place.number].filter(Boolean).join(' ');
        const cityParts = [place.postalCode, place.city].filter(Boolean).join(' ');
        const placeAddress = [addressParts, cityParts].filter(Boolean).join(', ') || place.placeName;

        setFormData(prev => ({
            ...prev,
            placeName: place.placeName,
            placeAddress: placeAddress,
            placeId: place.id || null, // Public DB ID
            placeUuid: place.placeUuid || null, // Public UUID
            latitude: place.latitude,
            longitude: place.longitude,
            country: place.country || '',
            countryCode: place.countryCode || ''
        }));
        setPlaceSearchQuery('');
        setPlaceSearchResults([]);
        setShowGoogleFallback(false);
    };

    // Handle Google address selection (fallback)
    const handleAddressChange = (_field, value) => {
        setFormData(prev => ({
            ...prev,
            placeAddress: value,
            placeName: value,
            placeId: null, // Not a Cabaza place yet
            localPlaceId: null // Will be created on submit
        }));
    };

    const handleCoordinatesChange = (lat, lng) => {
        setFormData(prev => ({
            ...prev,
            latitude: lat,
            longitude: lng
        }));
    };

    const handleAddressComponentsChange = (components) => {
        setFormData(prev => ({
            ...prev,
            street: components.street || components.route || '',
            number: components.number || components.street_number || '',
            postalCode: components.postalCode || components.postal_code || '',
            city: components.city || components.locality || '',
            country: components.country || '',
            countryCode: components.countryCode || '',
            googlePlaceId: components.place_id || components.placeId || ''
        }));
    };

    // Clear selected place
    const handleClearPlace = () => {
        setFormData(prev => ({
            ...prev,
            placeName: '',
            placeAddress: '',
            placeId: null,
            latitude: null,
            longitude: null,
            country: '',
            countryCode: ''
        }));
        setShowGoogleFallback(false);
    };

    // Toggle participant selection
    const toggleParticipant = (item) => {
        if (item.type === 'group') {
            const exists = selectedGroups.find(g => g.id === item.id);
            if (exists) {
                setSelectedGroups(prev => prev.filter(g => g.id !== item.id));
            } else {
                setSelectedGroups(prev => [...prev, { id: item.id, name: item.name, memberCount: item.memberCount || 0 }]);
            }
        } else {
            const exists = selectedParticipants.find(p => p.id === item.id);
            if (exists) {
                setSelectedParticipants(prev => prev.filter(p => p.id !== item.id));
            } else {
                setSelectedParticipants(prev => [...prev, { id: item.id, name: item.name, type: 'contact' }]);
            }
        }
        setInviteSearchQuery('');
        setInviteSearchResults([]);
    };

    const handleSubmit = async () => {
        if (!formData.title || !formData.startDate) return;

        setSaving(true);

        // Create event with place info (either Cabaza place ID or Google place address)
        const result = await createEvent({
            ...formData,
            isPublic: formData.isPublic
        }, currentUserId);

        if (result.success && result.eventId) {
            // Add individual participants
            for (const participant of selectedParticipants) {
                try {
                    await addEventParticipant(result.eventId, participant.id, 'invited');
                } catch (err) {
                    console.error('Error inviting contact:', err);
                }
            }

            // Add groups (which will add all their members)
            for (const group of selectedGroups) {
                try {
                    await addEventGroup(result.eventId, group.id);
                } catch (err) {
                    console.error('Error adding group:', err);
                }
            }

            onEventCreated();
        } else {
            alert(result.message || 'Error creating event');
        }
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onMouseDown={(e) => e.stopPropagation()}>
            <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
                onMouseDown={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-cabaza-purple to-cabaza-pink p-4 text-white rounded-t-xl flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-full">
                            <Plus className="w-5 h-5" />
                        </div>
                        <h3 className="text-lg font-bold">
                            {language === 'fr' ? 'Créer un événement' : 'Create Event'}
                        </h3>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form content - overflow-x-visible for DateTimePicker */}
                <div className="p-4 space-y-4 overflow-y-auto overflow-x-visible flex-1">
                {/* Public/Private Toggle */}
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, isPublic: true }))}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors ${
                            formData.isPublic
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        <Globe className="w-5 h-5" />
                        {language === 'fr' ? 'Public' : 'Public'}
                    </button>
                    <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, isPublic: false }))}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors ${
                            !formData.isPublic
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        <Lock className="w-5 h-5" />
                        {language === 'fr' ? 'Privé' : 'Private'}
                    </button>
                </div>

                {formData.isPublic ? (
                    <p className="text-sm text-gray-500 bg-green-50 p-2 rounded-lg">
                        {language === 'fr'
                            ? 'Visible par tous les membres Cabaza'
                            : 'Visible to all Cabaza members'}
                    </p>
                ) : (
                    <p className="text-sm text-gray-500 bg-orange-50 p-2 rounded-lg">
                        {language === 'fr'
                            ? 'Visible uniquement par les contacts invités'
                            : 'Visible only to invited contacts'}
                    </p>
                )}

                {/* Title */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'fr' ? 'Titre *' : 'Title *'}
                    </label>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                    />
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'fr' ? 'Description' : 'Description'}
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent resize-none"
                    />
                </div>

                {/* Start Date/Time */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Début *' : 'Start *'}
                        </label>
                        <DateTimePicker
                            value={formData.startDate}
                            onChange={(value) => setFormData(prev => ({ ...prev, startDate: value }))}
                            showTimeSelect={true}
                            minDate={new Date()}
                            placeholder={language === 'fr' ? 'Sélectionner date et heure' : 'Select date and time'}
                            open={openDatePicker === 'start'}
                            onCalendarOpen={() => setOpenDatePicker('start')}
                            onCalendarClose={() => setOpenDatePicker(null)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Fin' : 'End'}
                        </label>
                        <DateTimePicker
                            value={formData.endDate}
                            onChange={(value) => setFormData(prev => ({ ...prev, endDate: value }))}
                            showTimeSelect={true}
                            minDate={formData.startDate ? new Date(formData.startDate) : new Date()}
                            placeholder={language === 'fr' ? 'Sélectionner date et heure' : 'Select date and time'}
                            open={openDatePicker === 'end'}
                            onCalendarOpen={() => setOpenDatePicker('end')}
                            onCalendarClose={() => setOpenDatePicker(null)}
                        />
                    </div>
                </div>

                {/* Place Search - Cabaza places first */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            {language === 'fr' ? 'Lieu' : 'Place'}
                        </div>
                    </label>

                    {/* Selected place display */}
                    {formData.placeName ? (
                        <div className="bg-cabaza-purple/5 border border-cabaza-purple/20 rounded-lg p-3 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-800">{formData.placeName}</p>
                                {formData.placeAddress && formData.placeAddress !== formData.placeName && (
                                    <p className="text-xs text-gray-500">{formData.placeAddress}</p>
                                )}
                                {formData.placeId && (
                                    <span className="inline-flex items-center gap-1 text-xs text-cabaza-purple mt-1">
                                        <Building2 className="w-3 h-3" />
                                        {language === 'fr' ? 'Endroit Cabaza' : 'Cabaza Place'}
                                    </span>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={handleClearPlace}
                                className="p-1 hover:bg-gray-200 rounded-full"
                            >
                                <X className="w-4 h-4 text-gray-500" />
                            </button>
                        </div>
                    ) : (
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={placeSearchQuery}
                                onChange={(e) => setPlaceSearchQuery(e.target.value)}
                                placeholder={language === 'fr' ? 'Rechercher un endroit Cabaza...' : 'Search Cabaza place...'}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                            />

                            {/* Search results dropdown */}
                            {(placeSearchResults.length > 0 || searchingPlaces) && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {searchingPlaces ? (
                                        <div className="flex items-center justify-center py-4">
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cabaza-purple"></div>
                                        </div>
                                    ) : (
                                        placeSearchResults.map((place, index) => {
                                            // Build address display from components (handle both camelCase and lowercase keys)
                                            const street = place.street || place.STREET || '';
                                            const number = place.number || place.NUMBER || '';
                                            const postalCode = place.postalCode || place.POSTALCODE || '';
                                            const city = place.city || place.CITY || '';
                                            const placeName = place.placeName || place.PLACENAME || place.name || '';
                                            const placeId = place.id || place.ID || place.placeUuid || place.PLACEUUID || `place-${index}`;

                                            const addressParts = [street, number].filter(Boolean).join(' ');
                                            const cityParts = [postalCode, city].filter(Boolean).join(' ');
                                            const displayAddress = [addressParts, cityParts].filter(Boolean).join(', ');

                                            return (
                                                <button
                                                    key={placeId}
                                                    type="button"
                                                    onClick={() => handleSelectPlace(place)}
                                                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left"
                                                >
                                                    <Building2 className="w-4 h-4 text-cabaza-purple shrink-0" />
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-medium text-gray-800 truncate">{placeName || 'Lieu sans nom'}</p>
                                                        {displayAddress && (
                                                            <p className="text-xs text-gray-500 line-clamp-1">{displayAddress}</p>
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Google fallback when no Cabaza results */}
                    {showGoogleFallback && !formData.placeName && (
                        <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-2">
                                {language === 'fr'
                                    ? 'Aucun endroit Cabaza trouvé. Rechercher sur Google Maps :'
                                    : 'No Cabaza place found. Search on Google Maps:'}
                            </p>
                            <AddressAutocomplete
                                value={formData.placeAddress}
                                onChange={handleAddressChange}
                                onCoordinatesChange={handleCoordinatesChange}
                                onAddressComponentsChange={handleAddressComponentsChange}
                            />
                        </div>
                    )}
                </div>

                {/* Max Participants */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'fr' ? 'Nombre maximum de participants (0 = illimité)' : 'Max participants (0 = unlimited)'}
                    </label>
                    <input
                        type="number"
                        min="0"
                        value={formData.maxParticipants}
                        onChange={(e) => setFormData(prev => ({ ...prev, maxParticipants: parseInt(e.target.value) || 0 }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                    />
                </div>

                {/* Invite Contacts & Groups */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center gap-2">
                            <UserPlus className="w-4 h-4" />
                            {language === 'fr' ? 'Inviter des contacts ou groupes' : 'Invite contacts or groups'}
                        </div>
                    </label>

                    {/* Search input */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            value={inviteSearchQuery}
                            onChange={(e) => setInviteSearchQuery(e.target.value)}
                            placeholder={language === 'fr' ? 'Rechercher un contact ou groupe...' : 'Search contact or group...'}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                        />

                        {/* Search results dropdown */}
                        {(inviteSearchResults.length > 0 || searchingInvites || (inviteSearchQuery.length >= 2 && !searchingInvites)) && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                {searchingInvites ? (
                                    <div className="flex items-center justify-center py-4">
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cabaza-purple"></div>
                                    </div>
                                ) : inviteSearchResults.length === 0 ? (
                                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                        {language === 'fr' ? 'Aucun profil Cabaza trouvé' : 'No Cabaza profile found'}
                                    </div>
                                ) : (
                                    inviteSearchResults.map(item => {
                                        const isGroup = item.type === 'group';
                                        const isSelected = isGroup
                                            ? selectedGroups.some(g => g.id === item.id)
                                            : selectedParticipants.some(p => p.id === item.id);
                                        // Build display name from firstName/lastName or groupName
                                        const displayName = isGroup
                                            ? (item.groupName || item.name)
                                            : [item.firstName, item.lastName].filter(Boolean).join(' ') || item.name;

                                        return (
                                            <button
                                                key={`${item.type}-${item.id}`}
                                                type="button"
                                                onClick={() => toggleParticipant({ ...item, name: displayName })}
                                                className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left ${
                                                    isSelected ? 'bg-cabaza-purple/5' : ''
                                                }`}
                                            >
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                                    isGroup ? 'bg-blue-100' : 'bg-gray-100'
                                                }`}>
                                                    {isGroup ? (
                                                        <Users className="w-4 h-4 text-blue-600" />
                                                    ) : (
                                                        <User className="w-4 h-4 text-gray-600" />
                                                    )}
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-gray-800">{displayName}</p>
                                                    {isGroup && item.memberCount > 0 && (
                                                        <p className="text-xs text-gray-500">
                                                            {item.memberCount} {language === 'fr' ? 'membre(s)' : 'member(s)'}
                                                        </p>
                                                    )}
                                                </div>
                                                {isSelected && (
                                                    <Check className="w-4 h-4 text-cabaza-purple" />
                                                )}
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>

                    {/* Selected participants & groups */}
                    {(selectedParticipants.length > 0 || selectedGroups.length > 0) && (
                        <div className="flex flex-wrap gap-2 mt-2">
                            {selectedGroups.map(group => (
                                <span
                                    key={`group-${group.id}`}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs"
                                >
                                    <Users className="w-3 h-3" />
                                    {group.name}
                                    <button
                                        type="button"
                                        onClick={() => setSelectedGroups(prev => prev.filter(g => g.id !== group.id))}
                                        className="hover:bg-blue-200 rounded-full p-0.5"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                            {selectedParticipants.map(participant => (
                                <span
                                    key={`contact-${participant.id}`}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs"
                                >
                                    <User className="w-3 h-3" />
                                    {participant.name}
                                    <button
                                        type="button"
                                        onClick={() => setSelectedParticipants(prev => prev.filter(p => p.id !== participant.id))}
                                        className="hover:bg-gray-200 rounded-full p-0.5"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-4 flex justify-end gap-3 bg-gray-50 rounded-b-xl">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                        {language === 'fr' ? 'Annuler' : 'Cancel'}
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={saving || !formData.title || !formData.startDate}
                        className="px-4 py-2 bg-cabaza-purple text-white rounded-lg hover:bg-cabaza-purple/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                        {language === 'fr' ? 'Créer l\'événement' : 'Create Event'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Edit Event Modal Component
 */
const EditEventModal = ({ event, onClose, onEventUpdated, currentUserId, language, openDatePicker, setOpenDatePicker }) => {
    const [formData, setFormData] = useState({
        title: event.title || '',
        description: event.description || '',
        startDate: event.startDate || '',
        endDate: event.endDate || '',
        placeName: event.placeName || '',
        placeAddress: event.placeAddress || '',
        latitude: event.latitude || null,
        longitude: event.longitude || null,
        country: event.country || '',
        countryCode: event.countryCode || '',
        maxParticipants: event.maxParticipants || 0,
        isPublic: event.isPublic !== false // Default to true if undefined
    });
    const [saving, setSaving] = useState(false);

    // Handle address change from AddressAutocomplete
    const handleAddressChange = (_field, value) => {
        setFormData(prev => ({
            ...prev,
            placeAddress: value,
            placeName: value
        }));
    };

    // Handle coordinates from AddressAutocomplete
    const handleCoordinatesChange = (lat, lng) => {
        setFormData(prev => ({
            ...prev,
            latitude: lat,
            longitude: lng
        }));
    };

    // Handle address components from AddressAutocomplete
    const handleAddressComponentsChange = (components) => {
        setFormData(prev => ({
            ...prev,
            street: components.street || components.route || '',
            number: components.number || components.street_number || '',
            postalCode: components.postalCode || components.postal_code || '',
            city: components.city || components.locality || '',
            country: components.country || '',
            countryCode: components.countryCode || '',
            googlePlaceId: components.place_id || components.placeId || ''
        }));
    };

    const handleSubmit = async () => {
        if (!formData.title || !formData.startDate) return;

        setSaving(true);

        const result = await updateEvent(event.eventUuid, {
            ...formData,
            creatorId: currentUserId
        });

        if (result.success) {
            onEventUpdated();
        } else {
            alert(result.message || 'Error updating event');
        }
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onMouseDown={(e) => e.stopPropagation()}>
            <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
                onMouseDown={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-cabaza-purple to-cabaza-pink p-4 text-white rounded-t-xl flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-full">
                            <Pencil className="w-5 h-5" />
                        </div>
                        <h3 className="text-lg font-bold">
                            {language === 'fr' ? 'Modifier l\'événement' : 'Edit Event'}
                        </h3>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form content - overflow-x-visible for DateTimePicker */}
                <div className="p-4 space-y-4 overflow-y-auto overflow-x-visible flex-1">
                {/* Public/Private Toggle */}
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, isPublic: true }))}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors ${
                            formData.isPublic
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        <Globe className="w-5 h-5" />
                        {language === 'fr' ? 'Public' : 'Public'}
                    </button>
                    <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, isPublic: false }))}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors ${
                            !formData.isPublic
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        <Lock className="w-5 h-5" />
                        {language === 'fr' ? 'Privé' : 'Private'}
                    </button>
                </div>

                {/* Title */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'fr' ? 'Titre *' : 'Title *'}
                    </label>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                    />
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'fr' ? 'Description' : 'Description'}
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent resize-none"
                    />
                </div>

                {/* Start Date/Time */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Début *' : 'Start *'}
                        </label>
                        <DateTimePicker
                            value={formData.startDate}
                            onChange={(value) => setFormData(prev => ({ ...prev, startDate: value }))}
                            showTimeSelect={true}
                            placeholder={language === 'fr' ? 'Sélectionner date et heure' : 'Select date and time'}
                            open={openDatePicker === 'start'}
                            onCalendarOpen={() => setOpenDatePicker('start')}
                            onCalendarClose={() => setOpenDatePicker(null)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Fin' : 'End'}
                        </label>
                        <DateTimePicker
                            value={formData.endDate}
                            onChange={(value) => setFormData(prev => ({ ...prev, endDate: value }))}
                            showTimeSelect={true}
                            minDate={formData.startDate ? new Date(formData.startDate) : undefined}
                            placeholder={language === 'fr' ? 'Sélectionner date et heure' : 'Select date and time'}
                            open={openDatePicker === 'end'}
                            onCalendarOpen={() => setOpenDatePicker('end')}
                            onCalendarClose={() => setOpenDatePicker(null)}
                        />
                    </div>
                </div>

                {/* Place Search using AddressAutocomplete */}
                <AddressAutocomplete
                    label={language === 'fr' ? 'Lieu (rechercher un établissement ou une adresse)' : 'Place (search for a venue or address)'}
                    value={formData.placeAddress}
                    onChange={handleAddressChange}
                    onCoordinatesChange={handleCoordinatesChange}
                    onAddressComponentsChange={handleAddressComponentsChange}
                />

                {/* Selected Place Info */}
                {formData.placeAddress && formData.latitude && (
                    <div className="bg-cabaza-purple/5 border border-cabaza-purple/20 rounded-lg p-3">
                        <p className="text-sm text-gray-600">{formData.placeAddress}</p>
                        {formData.country && (
                            <p className="text-xs text-gray-500 mt-1">{formData.country}</p>
                        )}
                    </div>
                )}

                {/* Max Participants */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'fr' ? 'Nombre maximum de participants (0 = illimité)' : 'Max participants (0 = unlimited)'}
                    </label>
                    <input
                        type="number"
                        min="0"
                        value={formData.maxParticipants}
                        onChange={(e) => setFormData(prev => ({ ...prev, maxParticipants: parseInt(e.target.value) || 0 }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                    />
                </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-4 flex justify-end gap-3 bg-gray-50 rounded-b-xl">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                        {language === 'fr' ? 'Annuler' : 'Cancel'}
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={saving || !formData.title || !formData.startDate}
                        className="px-4 py-2 bg-cabaza-purple text-white rounded-lg hover:bg-cabaza-purple/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                        {language === 'fr' ? 'Enregistrer' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Events;
